import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/marketing/SEO";

const Lausanne = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Piktable Lausanne",
    "description": "Solution de gestion de réservations pour restaurants à Lausanne",
    "url": "https://piktable.ch/lausanne",
    "areaServed": {
      "@type": "City",
      "name": "Lausanne",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Canton de Vaud"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "46.5197",
      "longitude": "6.6323"
    },
    "priceRange": "CHF 10-99",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Lausanne",
      "addressCountry": "CH"
    },
    "email": "contact@piktable.ch",
    "sameAs": ["https://www.instagram.com/piktable"],
    "image": "https://piktable.ch/screenshots/dashboard-overview.png"
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://piktable.ch/" },
      { "@type": "ListItem", "position": 2, "name": "Lausanne", "item": "https://piktable.ch/lausanne" }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Gestion de réservations pour restaurants à Lausanne"
        description="Piktable aide les restaurants lausannois à gérer leurs réservations. Solution locale développée à Lausanne. 49 CHF/mois tout inclus, ou 29 CHF à vie pour les 50 fondateurs. Zéro commission par couvert."
        canonical="/lausanne"
        structuredData={[structuredData, breadcrumbData]}
      />
      <Header />
      <main className="pt-16">
        <section className="py-24 bg-gradient-hero">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="font-playfair text-5xl md:text-6xl font-bold text-foreground">
                Gestion de réservations pour restaurants à Lausanne
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Piktable est né à Lausanne pour aider les cafés, bistrots et restaurants vaudois à gérer leurs réservations simplement. Une solution locale, pensée pour les établissements de la région lémanique.
              </p>
              <div className="flex justify-center pt-6">
                <Button size="lg" onClick={() => navigate("/signup")}>
                  Essayer gratuitement
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="text-center">
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Pourquoi choisir Piktable à Lausanne ?
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🇨🇭</div>
                  <h3 className="text-2xl font-bold text-foreground">Solution 100% locale</h3>
                  <p className="text-muted-foreground">
                    Développé à Lausanne avec une compréhension parfaite des besoins des restaurants de la région. Support en français, facturation en CHF.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">💰</div>
                  <h3 className="text-2xl font-bold text-foreground">Tarification transparente</h3>
                  <p className="text-muted-foreground">
                    49 CHF/mois tout inclus, ou 29 CHF à vie pour les 50 fondateurs du Printemps 2026. Zéro commission par couvert, pas de frais cachés.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">⚡</div>
                  <h3 className="text-2xl font-bold text-foreground">Simple et efficace</h3>
                  <p className="text-muted-foreground">
                    Interface intuitive pensée pour les petites équipes. Pas besoin de formation, vous êtes opérationnel en 10 minutes.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🎁</div>
                  <h3 className="text-2xl font-bold text-foreground">1 mois gratuit</h3>
                  <p className="text-muted-foreground">
                    Testez Piktable gratuitement pendant un mois complet, sans engagement et sans carte bancaire.
                  </p>
                </div>
              </div>

              <div className="mt-12 space-y-4">
                <h3 className="font-playfair text-2xl font-bold text-foreground">
                  Piktable est né à Lausanne
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Notre équipe est basée à Lausanne et connaît la scène gastronomique lausannoise de l'intérieur. Du Flon à Ouchy, du centre-ville à Pully, nous comprenons les réalités des restaurateurs vaudois : les variations saisonnières liées au tourisme, les terrasses estivales en bord de lac, et le rythme des quartiers d'affaires à midi. En tant que solution locale, nous offrons un support de proximité en français et une réactivité que les plateformes internationales ne peuvent pas garantir. Chaque fonctionnalité de Piktable a été pensée en collaboration avec des restaurateurs de la région lausannoise.
                </p>
              </div>

              <div className="mt-16 p-8 rounded-2xl bg-primary/5 border-2 border-primary/20">
                <h3 className="font-playfair text-3xl font-bold text-foreground mb-4 text-center">
                  Parfait pour les établissements lausannois
                </h3>
                <p className="text-lg text-muted-foreground text-center mb-8">
                  Que vous teniez un bistrot au Flon, un café à Ouchy ou un restaurant dans le centre-ville, Piktable s'adapte à votre rythme et à vos besoins.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
                    Démarrer mon mois gratuit
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full sm:w-auto"
                  >
                    En savoir plus
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Lausanne;
